import type { ProficiencyLevelResponse } from '@/api/performance-profile/performanceProfileService';
import type { ProficiencyLevelType } from '@/constants/ProficiencyLevel';
import type { NextLevelSkill } from '@/types/career-growth';

import { proficiencyLevels as EXPECTED_LEVELS } from '@/constants/ProficiencyLevel';
import { orderProficiencyLevels } from '@/utils/proficiencyLevel';

/** One competency of the target profile, placed on the company's proficiency scale. */
export interface SkillReadinessItem {
    skill: NextLevelSkill;
    /** `0` when the competency was never assessed, `1` at the top of the scale. */
    fraction: number;
    /** The level last assessed, or `null` when there is none (or it left the catalogue). */
    level: ProficiencyLevelResponse | null;
    /** How far below the level the profile requires, on the same `0`-`1` scale; `0` once reached. */
    gap: number;
}

export interface SkillReadiness {
    /**
     * `0` to `100` over every competency of the profile. `null` when there is nothing to place: a
     * profile that lists no competency, or one where none has been assessed yet.
     */
    score: number | null;
    /** Every competency of the profile, in reading order: {@link gaps} first, then the rest. */
    items: SkillReadinessItem[];
    /** The competencies still below their required level, the widest gap first. */
    gaps: SkillReadinessItem[];
}

/**
 * The required level on the same `0`-`1` scale as an assessed one. The profile's requirement is
 * one of five fixed expectations (Junior to Expert) while the company picks its own number of
 * levels, so both are read as a share of their own scale: "Satisfactory", the third of five,
 * asks for 60% of the way up.
 */
export const requiredFraction = (expectedLevel: ProficiencyLevelType): number =>
    requiredRank(expectedLevel) / EXPECTED_LEVELS.length;

/** 1 for Junior to 5 for Expert; an expectation this list does not know asks for the top. */
const requiredRank = (expectedLevel: ProficiencyLevelType): number => {
    const index = EXPECTED_LEVELS.indexOf(expectedLevel);
    return index === -1 ? EXPECTED_LEVELS.length : index + 1;
};

/**
 * How ready the employee is for the next level: each competency is placed on the company's
 * proficiency scale, and the score is the average of those fractions over the whole profile.
 * `sortOrder` 0 is the top level, so the best level reads `1` and the lowest reads
 * `1 / scale length`. The competencies left to develop are the ones below their requirement, all
 * of them, the widest gap first; a competency never assessed is as far as it gets.
 *
 * `items` comes back in that same reading order -- what is missing first, then what is already
 * there. The screen shows the whole profile, not only the gaps: a reader told the employee is
 * ready has to be able to see the competencies that say so.
 */
export const computeSkillReadiness = (
    skills: NextLevelSkill[],
    levels: ProficiencyLevelResponse[],
): SkillReadiness => {
    const scale = orderProficiencyLevels(levels);

    const items = skills.map((skill) => {
        const rank = scale.findIndex((level) => level.id === skill.proficiency?.id);
        const level = rank === -1 ? null : scale[rank];
        const reachedRank = level ? scale.length - rank : 0;
        // Both shares over one common denominator, in whole numbers: two equal gaps are then the
        // very same number, and keep the server's order instead of a rounding error's.
        const gapUnits =
            requiredRank(skill.expectedLevel) * scale.length - reachedRank * EXPECTED_LEVELS.length;

        return {
            skill,
            level,
            fraction: reachedRank / scale.length,
            gap: Math.max(0, gapUnits) / (EXPECTED_LEVELS.length * scale.length),
        };
    });

    // `sort` is stable: equal gaps keep the server's order, priorities first.
    const gaps = items.filter((item) => item.gap > 0).sort((a, b) => b.gap - a.gap);
    const reached = items.filter((item) => item.gap === 0);
    const ordered = [...gaps, ...reached];

    // Nothing assessed is not a score of zero. Zero is a place on the journey -- its very start --
    // and putting someone there states something about them that no evaluation ever said. The bar
    // is then not drawn at all, and the screen says what is missing instead.
    if (items.every((item) => item.level === null)) {
        return { score: null, items: ordered, gaps };
    }

    const total = items.reduce((sum, item) => sum + item.fraction, 0);

    return { score: Math.round((total / items.length) * 100), items: ordered, gaps };
};
